import { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { loadTickerSearchList, getTickersBySymbols } from '../../store/slices/globalResources.slice';

// Single shared promise for loading
let globalLoadingPromise = null;

export const useTickerSearchIndex = (symbols = []) => {
  const dispatch = useDispatch();
  const mountedRef = useRef(true);
  const initializingRef = useRef(false);

  // Memoize symbols array to prevent unnecessary selector updates
  const symbolsRef = useRef(symbols);

  const status = useSelector((state) => state.global.status.tickerSearchList);
  const tickers = useSelector(
    (state) => getTickersBySymbols(state, symbolsRef.current)
  );

  // Local loading state just for initial data fetch
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);

  useEffect(() => {
    symbolsRef.current = symbols;
  }, [symbols]);

  useEffect(() => {
    mountedRef.current = true;

    const initializeData = async () => {
      // Prevent multiple initialization attempts
      if (initializingRef.current || status === 'loading' || status === 'succeeded') {
        return;
      }

      initializingRef.current = true;
      setIsLoadingInitial(true);

      try {
        // Reuse existing promise or create new one
        globalLoadingPromise = globalLoadingPromise || dispatch(loadTickerSearchList()).unwrap();
        await globalLoadingPromise;
      } catch (error) {
        console.error('Failed to load ticker data:', error);
      } finally {
        if (mountedRef.current) {
          setIsLoadingInitial(false);
          globalLoadingPromise = null;
          initializingRef.current = false;
        }
      }
    };

    initializeData();

    return () => {
      mountedRef.current = false;
    };
  }, [dispatch, status]);

  return {
    tickers,
    loading: isLoadingInitial || status === 'loading',
    isInitialized: status === 'succeeded',
    error: status === 'failed'
  };
};